import type { DateRange } from './trip'

/**
 * A unique identifier for the brand type.
 *
 * This symbol is used to quickly identify the type of an itinerary item
 * without having to check various property combinations.
 */
export const BrandTypeId: unique symbol = Symbol.for('Brand')

export type ItineraryWaypointPlaceItem = {
  type: 'place'
  /**
   * The unique identifier for the item in the itinerary.
   */
  id: string
  name: string
  subtitle?: string
  icon?: string
  picture?: string
  parent: 'waypoint'
  waypointId: string
  poiId: string
  [BrandTypeId]: 'WaypointPlace'
}

export type ItineraryDayPlaceItem = {
  type: 'place'
  /**
   * The unique identifier for the item in the itinerary.
   */
  id: string
  name: string
  subtitle?: string
  icon?: string
  picture?: string
  parent: 'day'
  waypointId: string
  dayId: string
  itemId: string
  [BrandTypeId]: 'DayPlace'
}

export type ItineraryDayItem = {
  type: 'day'
  /**
   * The unique identifier for the item in the itinerary.
   */
  id: string
  parent: 'waypoint'
  date: string
  dayId: string
  waypointId: string
  [BrandTypeId]: 'Day'
}

export type ItineraryPlaceItem =
  | ItineraryWaypointPlaceItem
  | ItineraryDayPlaceItem

export type ItineraryWaypointItem = {
  type: 'waypoint'
  /**
   * The unique identifier for the item in the itinerary.
   */
  id: string
  parent: 'trip'
  waypointId: string
  name: string
  picture?: string
  startDate?: DateRange
  endDate?: DateRange
  summary?: string | null
  [BrandTypeId]: 'Waypoint'
}

export type ItineraryItem =
  | ItineraryWaypointItem
  | ItineraryPlaceItem
  | ItineraryDayItem

export function isItineraryItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryItem {
  return Boolean(item[BrandTypeId])
}

export function isPlaceItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryPlaceItem {
  return (
    item[BrandTypeId] === 'WaypointPlace' || item[BrandTypeId] === 'DayPlace'
  )
}

export function isDayPlaceItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryDayPlaceItem {
  return item[BrandTypeId] === 'DayPlace'
}

export function isWaypointPlaceItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryWaypointPlaceItem {
  return item[BrandTypeId] === 'WaypointPlace'
}

export function isDayItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryDayItem {
  return item[BrandTypeId] === 'Day'
}

export function isWaypointItem(
  item: Record<string | symbol, unknown>,
): item is ItineraryWaypointItem {
  return item[BrandTypeId] === 'Waypoint'
}
